import React, { useState, KeyboardEvent } from 'react';
import { useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { Form } from 'react-final-form';
// images
import {
  BackArrow,
  ForwardArrow,
} from '@/components/landing-page-templates/ConfigLandingPage/LandingPageIcons';
// api
import api from '@/api';
// types
import { IEventSSOLoginFormProps } from './types';
import { IAccountRegistrationEnum } from '@/models/account/types';
import { IEventCheckinType, ILoginType } from '@/components/landing-page-templates/ConfigLandingPage/types';
// utils
import { debounce } from 'lodash';
import { isValidEmail } from '@/utils/jsHelpers/validator';
// hooks
import { useMemoizedSelector } from '@/hooks/use-memoized-selector';
import { makeSelectEventById } from '@/models/event';
// styles
import styles from './styles.module.scss';
// components
import Loader from '@/components/ui/Loader';
import FormSection from '@/components/forms/FormSection';
import TermsAndConditions from '@/components/landing-page-templates/ConfigLandingPage/DisplayMessageComponents/TermsAndConditions';
// config
import { FORM_FIELDS } from './config';
import { redirectToSsoLoginPage } from '@/components/landing-page-templates/ConfigLandingPage/utils';

const EventSSOLoginForm = ({
  buttonStyle,
  updateLoginMethod,
  updateEmail,
  email,
  eventEntryType,
}: IEventSSOLoginFormProps) => {
  const { eventId } = useParams<{ eventId: string }>();
  const event = useMemoizedSelector(makeSelectEventById, eventId);
  const { organizationId } = event;

  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const initialValues = {
    inputEmail: email,
  };

  // TODO - Make this function DRY as it's repeated now.
  const checkInviteOnlyAccess = async (trimmedEmail: string) => {
    try {
      const { data } = await api.eventRole.checkInviteOnlyAccess(trimmedEmail, eventId);
      // NOTE - the backend API returns a 'boolean' and the frontend treats it as a boolean but adding a check for the string type just in case
      if (data === 'false' || data === false) {
        dispatch({
          type: 'account/setRegistrationStep',
          payload: {
            registrationStep: IAccountRegistrationEnum.NO_ACCESS,
          },
        });
        updateLoginMethod(ILoginType.EMAIL);
      } else {
        redirectToSsoLoginPage(eventId, organizationId, dispatch);
      }
    } catch (error) {
      // Failed to check if the email is invited (event role exists) for this event
      setLoading(false);
      dispatch({
        type: 'global/addDangerToast',
        payload: { description: 'Something went wrong, please try again...' },
      });
    }
  };

  // The handler for clicking the button or when Enter key is pressed
  const handleFormSubmit = ({ inputEmail }) => {
    setLoading(true);
    const trimmedEmail: string = inputEmail.trim();
    if (email !== trimmedEmail) {
      updateEmail(trimmedEmail);
    }
    // If invite only event, check if email has event role
    if (eventEntryType === IEventCheckinType.INVITE_ONLY) {
      checkInviteOnlyAccess(trimmedEmail);
    } else {
      redirectToSsoLoginPage(eventId, organizationId, dispatch);
    }
  };

  // The handler for a click on the back arrow
  const handleBackClick = () => {
    updateLoginMethod(undefined);
  };

  // The handler for the Enter key while focused on back arrow button
  const handleEnterKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      updateLoginMethod(undefined);
    }
  };

  const handleOnChange = debounce(target => {
    // Check if email has changed from the state and if email is valid
    if (target.value !== email && isValidEmail(target.value)) {
      updateEmail(target.value);
    }
  }, 300);

  return (
    <div>
      <div className={styles.sectionHeader}>
        {/* Back Arrow SVG */}
        <div
          role="button"
          tabIndex={0}
          className={styles.backArrow}
          onClick={handleBackClick}
          onKeyUp={handleEnterKey}
        >
          <BackArrow />
        </div>
        <p className={styles.sectionLabel}>Enter the Event</p>
      </div>
      <Form
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, submitting }) => (
          <form
            onSubmit={handleSubmit}
            onChange={e => handleOnChange(e.target)}
          >
            <FormSection formData={FORM_FIELDS} />

            <div className={styles.button}>
              <button
                type="submit"
                onSubmit={handleSubmit}
                onClick={handleSubmit}
                onKeyPress={handleSubmit}
                disabled={submitting || loading}
                style={buttonStyle}
                className={styles.submitButton}
              >
                {/* loader */}
                {loading && <Loader />}
                {/* Forward Arrow SVG */}
                <ForwardArrow color={buttonStyle.color}/>
                <p className={styles.buttonText}>Continue</p>
              </button>
            </div>
          </form>
        )}
      />

      <TermsAndConditions />
    </div>
  );
};
export default EventSSOLoginForm;
